import { AppData, Lightning, Registry } from '@lightningjs/sdk';
import LightningPlayer from 'components/pages/playback/player-components/LightningPlayer';
import { EpgChannel } from 'types/api/media';
import FastPlaybackOverlay from 'components/pages/epg/FastPlaybackOverlay';
import { debounce } from 'support/generalUtils';
import { constants } from 'aliases';
import LoadingSpinner from 'components/common/LoadingSpinner';
import { StaticViewContexts, ViewContext } from 'types/analytics';
import { secondsToMilliseconds } from 'support/timeUtils';
import EpgPlayerEventHandler from 'components/pages/playback/PlayerEventHandler/EpgPlayerEventHandler';
import { getFocusDepth } from 'support/generalUtils';
import TextTrackDisplay from 'components/pages/playback/TextTrackDisplay';
import { PageId } from 'types/pageId';
import { isCCEnabled } from 'support/playerUtils';
import { reportClosedCaptionState } from 'services/analytics/reportingServiceVideo';

export interface EpgPlayerTemplateSpec
  extends Lightning.Component.TemplateSpec {
  channel: EpgChannel | null;
  pageId: PageId | null;
  absoluteY: number;
  absoluteX: number;
  xAbsoluteOffset: number;
  viewContext: ViewContext;

  Content: {
    Player: typeof LightningPlayer;
    PlayerEventHandler: typeof EpgPlayerEventHandler;
    Overlay: typeof FastPlaybackOverlay;
    LoadingSpinner: typeof LoadingSpinner;
    TextTrackDisplay: typeof TextTrackDisplay;
  };
}

export const MINI_PLAYER_X = 1178;
export const MINI_PLAYER_Y = 147;
export const MINI_PLAYER_WIDTH = 562;
export const MINI_PLAYER_HEIGHT = 313;
export const MINI_PLAYER_RADIUS = 10;

const MINI_PLAYER_LOADING_SPINNER_X = MINI_PLAYER_WIDTH / 2;
const MINI_PLAYER_LOADING_SPINNER_Y = MINI_PLAYER_HEIGHT / 2;

const FULL_SCREEN_PLAYER_X = 0;
const FULL_SCREEN_PLAYER_Y = 0;
const FULL_SCREEN_PLAYER_WIDTH = 1920;
const FULL_SCREEN_PLAYER_HEIGHT = 1080;

const FULL_SCREEN_PLAYER_LOADING_SPINNER_X = FULL_SCREEN_PLAYER_WIDTH / 2;
const FULL_SCREEN_PLAYER_LOADING_SPINNER_Y = FULL_SCREEN_PLAYER_HEIGHT / 2;

const INTERACTIVE_TO_IDLE_TRANSITION_TIME = 5000;

export default class EpgPlayer
  extends Lightning.Component<EpgPlayerTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<EpgPlayerTemplateSpec>
{
  private _Content = this.getByRef('Content')!;
  private _Player = this._Content.getByRef('Player')!;
  private _PlayerEventHandler = this._Content.getByRef('PlayerEventHandler')!;
  private _Overlay = this._Content.getByRef('Overlay')!;
  private _LoadingSpinner = this._Content.getByRef('LoadingSpinner')!;
  private _TextTrackDisplay = this._Content.getByRef('TextTrackDisplay')!;

  viewContext = StaticViewContexts.EPG;

  private _channel: EpgChannel | null = null;
  private _pageId: PageId | null = null;
  private _ccEnabled = false;
  private _minifyTimeout: number | undefined;

  private _restartIdleTimer = debounce(() => {
    const isCcOverlayOpen = this._Overlay.isCcOverlayOpen();

    if (!!AppData?.announcerService.getIsSpeaking?.()) {
      Registry.setTimeout(this._restartIdleTimer, secondsToMilliseconds(1));
    } else if (
      this._getState() === 'FullscreenInteractive' &&
      !isCcOverlayOpen
    ) {
      this._setState('FullscreenIdle');
    }
  }, INTERACTIVE_TO_IDLE_TRANSITION_TIME);

  xAbsoluteOffset = 0;

  set channel(channel: EpgChannel | null) {
    // don't update the channel if it's currently selected
    if (!!channel?.id && channel.id === this._channel?.id) return;

    this._Overlay.patch({ channel });

    this._channel = channel;
    this.updateChannel();
  }

  get channel() {
    return this._channel;
  }

  set pageId(pageId: PageId) {
    this._pageId = pageId;
  }

  get player() {
    return this._Player;
  }

  set absoluteX(x: number) {
    this.x = x - this.xAbsoluteOffset;
    this._Player.patch({ absoluteX: x });
  }

  set absoluteY(y: number) {
    this.y = y;
    this._Player.patch({ absoluteY: y });
  }

  override set w(w: number) {
    super.w = w;
    this._Player.patch({ w });
  }

  override set h(h: number) {
    super.h = h;
    this._Player.patch({ h });
  }

  static override _template(): Lightning.Component.Template<EpgPlayerTemplateSpec> {
    return {
      Content: {
        Player: {
          type: LightningPlayer,
        },
        PlayerEventHandler: {
          type: EpgPlayerEventHandler,
          signals: {
            $bufferStart: '$bufferStart',
            $bufferEnd: '$bufferEnd',
            $fatalError: '$fatalError',
            $streamAdaption: '$streamAdaption',
          },
        },
        TextTrackDisplay: {
          type: TextTrackDisplay,
        },
        Overlay: {
          type: FastPlaybackOverlay,
          passSignals: {
            backToGuide: true,
          },
          signals: {
            ccAudio: '$ccAudio',
            onSubtitlesChange: '$onSubtitlesChange',
            onAudioChange: '$onAudioChange',
          },
        },
        LoadingSpinner: {
          type: LoadingSpinner,
          visible: false,
        },
      },
    };
  }

  override _setup() {
    this._ccEnabled = isCCEnabled();
  }

  override _active() {
    this._Player.open();

    window.backgroundingService.addBackgroundingAction(
      'playbackBackgrounded',
      this._onBackgrounded.bind(this),
    );
    window.backgroundingService.addForegroundingAction(
      'playbackForegrounded',
      this._onForegrounded.bind(this),
    );
  }

  override _enable() {
    // TODO: update this logic if we need to support swapping between brightcove and lightning players in EPG
    this._PlayerEventHandler.patch({ playerType: 'lightning' });
    this._Player.initialize(this._PlayerEventHandler.getConsumer());
  }

  override _inactive() {
    this.cleanupPlayer();
    this.clearMinifyTimeout();
    this._TextTrackDisplay._disable();

    window.backgroundingService.removeBackgroundingAction(
      'playbackBackgrounded',
    );
    window.backgroundingService.removeForegroundingAction(
      'playbackForegrounded',
    );
  }

  private _onBackgrounded() {
    this._Player.pause();
  }

  private _onForegrounded() {
    this._Player.resume();
  }

  private _handleClick() {
    if (this._getState() === 'FullscreenIdle') {
      this._setState('FullscreenInteractive');
    }
  }
  private hideCc() {
    this._Player.ccSuppressed = true;
    this._TextTrackDisplay.reset();
  }

  private showCc() {
    this._Player.ccSuppressed = false;
  }

  private async updateChannel() {
    this.cleanupPlayer();
    if (!this._channel) return;

    const { isDrm } = this._channel;
    this._Player.hasDrm = isDrm;

    let streamUrl = this._channel.streamUrl;
    if (isDrm) {
      const drmService = AppData?.drmService;
      const drmItem = drmService?.getDashItem(this._channel.drmItems);

      if (drmItem) {
        try {
          const drmData = await drmService!.getDRMData(drmItem);

          this._Player.drmLicenseUrl = drmData?.licenseUrl;
          streamUrl = drmData?.videoSrc ?? streamUrl;
        } catch (e) {
          console.error(e);
          // Do nothing, attempt to use the channel's stream url. Stream errors
          // will still be caught in the Player layer
        }
      }
    }

    this._PlayerEventHandler.initialize(
      this._Player,
      this._channel,
      this._pageId,
      this.viewContext,
      undefined,
    );

    this._Player.liveStreamUrl = streamUrl || null;
    this._Player.liveStreamType = this._channel.streamType;
    this._Player.adZone = this._channel.adZone;
    this._Player.open();

    if (AppData?.device.useTextTrackComponent()) {
      this.setUpTextTrackDisplay();
    }
  }

  private setUpTextTrackDisplay() {
    this._Player.onStreamLoaded = () => {
      if (this._Player.videoElement) {
        this._TextTrackDisplay.initializeCaption(
          this._Player.videoElement as HTMLVideoElement,
        );
      }
    };

    AppData?.device.handleCcChange((enabled: boolean) => {
      if (enabled) {
        this._TextTrackDisplay.initializeCaption(
          this._Player.videoElement as HTMLVideoElement,
        );
      } else {
        this._TextTrackDisplay._disable();
      }
    });
  }

  private cleanupPlayer() {
    if (this._Player.isClosed) return;

    this._PlayerEventHandler.handlePlayerEnd({
      hasReachedEnd: false,
    });
    this._Player.disableCc();
    this._TextTrackDisplay.reset();
    this._Player.close();
  }

  fullscreen() {
    //reenable cc when going back to fullscreen
    this.showCc();
    this.clearMinifyTimeout();
    this.patch({
      absoluteX: FULL_SCREEN_PLAYER_X,
      absoluteY: FULL_SCREEN_PLAYER_Y,
      w: FULL_SCREEN_PLAYER_WIDTH,
      h: FULL_SCREEN_PLAYER_HEIGHT,
    });
    this._LoadingSpinner.patch({
      x: FULL_SCREEN_PLAYER_LOADING_SPINNER_X,
      y: FULL_SCREEN_PLAYER_LOADING_SPINNER_Y,
    });

    this._setState('FullscreenIdle');
  }

  minify() {
    this.minifyPlayer(false);
    this._setState('Mini');
  }

  private minifyPlayer(useOffset: boolean) {
    //temp disable cc when player is minified
    this.hideCc();
    // Adding an offset to the miniplayer forces it to re-size
    const offset = useOffset ? 1 : 0;

    this.patch({
      // Note: The offset must be added to x & y values, does not work for w & h
      absoluteX: MINI_PLAYER_X + offset,
      absoluteY: MINI_PLAYER_Y + offset,
      w: MINI_PLAYER_WIDTH,
      h: MINI_PLAYER_HEIGHT,
    });
    this._LoadingSpinner.patch({
      x: MINI_PLAYER_LOADING_SPINNER_X,
      y: MINI_PLAYER_LOADING_SPINNER_Y,
    });
  }

  clearMinifyTimeout() {
    if (!this._minifyTimeout) return;
    Registry.clearTimeout(this._minifyTimeout);
    this._minifyTimeout = undefined;
  }

  updateTime(currentTime: Date) {
    this._Overlay.updateTime(currentTime);
  }

  pause() {
    this._Player.pause();
  }

  resume() {
    this._Player.resume();
  }

  $ccAudio() {
    const currSubtitle = AppData!.storageService.subtitle.get();
    const currAudio = AppData!.storageService.audio.get();
    const subtitles = this._Player.getSubtitles();

    let ccIndex = -1;
    let audioIndex = -1;

    if (subtitles.length) {
      if (currSubtitle) ccIndex = this._Player.getCcIndex(currSubtitle);
    }

    const audio = this._Player.getAudioOptions();
    if (currAudio) {
      audioIndex = audio.findIndex(audioOption => audioOption === currAudio);
    }
    // If no audio index selected but we have audio options, use the first one (Default)
    if (audioIndex === -1 && audio.length) {
      audioIndex = 0;
    }

    const cc = [constants.closedCaption.none, ...subtitles];

    if (subtitles.length && currSubtitle) {
      ccIndex = this._Player.getCcIndex(currSubtitle) + 1; // +1 to account for the none subtitle
    } else {
      ccIndex = 0;
    }

    this._Overlay.updateCc(ccIndex, audioIndex, cc, audio);
  }

  $onSubtitlesChange(option: string) {
    this._ccEnabled = option !== constants.closedCaption.none;
    if (!this._ccEnabled) {
      this._Player.disableCc();
      this._TextTrackDisplay.reset();
    } else if (this._getState() !== 'Mini') {
      this._Player.enableCc(option);
    }

    AppData!.storageService.subtitle.set(option);
    reportClosedCaptionState(this._ccEnabled);
  }

  private $fatalError() {
    this.cleanupPlayer();
    this.signal('$fatalError');
  }

  private $onAudioChange(option: string) {
    this._Player.setAudioTrack(option);
    AppData!.storageService.audio.set(option);
  }

  private $bufferStart() {
    this._LoadingSpinner.patch({ visible: true });
  }

  private $bufferEnd() {
    this._LoadingSpinner.patch({ visible: false });
  }

  static override _states() {
    return [
      class Mini extends this {
        override $enter() {
          this._Overlay.hide(false);
        }

        private $streamAdaption() {
          /**
           * On some older versions of Chrome, the video will appear as full screen on
           * stream quality change, ignoring the width & height. To account for this,
           * we are setting the miniplayer at a slight offset and re-setting it after
           * 3 seconds, ensuring the player is minified
           */
          this.minifyPlayer(true);
          this.clearMinifyTimeout();
          this._minifyTimeout = Registry.setTimeout(() => {
            this._minifyTimeout = undefined;
            this.minifyPlayer(false);
          }, secondsToMilliseconds(3));
        }
      },

      class FullscreenIdle extends this {
        override $enter() {
          this._Overlay.hide();
          this.signal('onFullscreenIdle');
        }

        override _captureKey() {
          this._setState('FullscreenInteractive');
          this.signal('onFullscreenInteractive');
        }
      },

      class FullscreenInteractive extends this {
        override $enter() {
          this._Overlay.show();
          this.fireAncestors(
            '$announcerRefresh',
            getFocusDepth(this.application, this),
          );
          this._restartIdleTimer();
        }

        override _captureKey() {
          this._restartIdleTimer();
          return false;
        }

        override _getFocused() {
          return this._Overlay;
        }
      },
    ];
  }
}
